import { useCallback, useMemo, useState } from "react";
import { endOfDay, formatISO, isValid, parseISO, startOfDay } from "date-fns";
import type { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { FiltersTypes } from "@common/types/filters/general";
import { View } from "@common/types/views/views";
import { useWorkspacesList } from "@api/workspace.api";
import { useChartOfAccountsTemplateAppliedEntities } from "@api/coaTemplate.api";
import { TemplateDrawerTrail } from "@components/settings/ChartOfAccountsTemplates/TemplateDrawerTrail";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { useScope } from "@state/scopeContext.lib";
import type { ApplyCoaTemplateEntitySelectionRow } from "../apply/applyCoaTemplateEntitySelection.types";
import {
    applyCoaTemplateEntitySelectionViewConfig,
    type ApplyCoaEntityOpenCallbacks,
} from "../apply/applyCoaTemplateEntitySelectionViewConfig";
import { useApplyCoaTemplateEntityTree } from "../apply/useApplyCoaTemplateEntityTree";
import {
    filterSelectEntitiesRows,
    filterWorkspaceRowsByServicesAndTeam,
} from "../apply/applyCoaTemplateSelectEntitiesFilters";
import { appliedEntitiesDrawerView } from "./appliedEntitiesDrawerView.constants";
import { AppliedEntitiesToolbar } from "./AppliedEntitiesToolbar";
import { openEntityChartOfAccounts } from "./openEntityChartOfAccounts";
import styles from "./ChartOfAccountsTemplateAppliedEntitiesDrawer.module.scss";

interface Props {
    open: boolean;
    template: ChartOfAccountsTemplate | null;
    onClose: () => void;
}

// Inclusive date-range test (day granularity) against an ISO value. A null value
// fails whenever a bound is set (e.g. entities with no recorded date).
function isWithinRange(
    iso: string | null,
    start: Date | undefined,
    end: Date | undefined,
): boolean {
    if (!start && !end) {
        return true;
    }
    if (!iso) {
        return false;
    }
    const value = parseISO(iso);
    if (!isValid(value)) {
        return false;
    }
    if (start && value < startOfDay(start)) {
        return false;
    }
    if (end && value > endOfDay(end)) {
        return false;
    }
    return true;
}

const ChartOfAccountsTemplateAppliedEntitiesDrawerContent = ({
    template,
}: {
    template: ChartOfAccountsTemplate;
}) => {
    const { organizationId } = useScope();
    const [view, setView] = useState<View>(appliedEntitiesDrawerView);
    const [searchFilter, setSearchFilter] = useState<string | undefined>(
        undefined,
    );
    const [filters, setFilters] = useState<FiltersTypes>({});

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (patch) => setFilters((current) => ({ ...current, ...patch })),
        [],
    );

    const {
        data: workspaces,
        isLoading: isWorkspacesLoading,
        isError: isWorkspacesError,
    } = useWorkspacesList({
        organizationId: organizationId ?? undefined,
        includeEntities: true,
    });

    const {
        data: appliedEntitiesData,
        isLoading: isAppliedLoading,
        isError: isAppliedError,
    } = useChartOfAccountsTemplateAppliedEntities(template.id);

    const allowedEntityIds = useMemo(
        () =>
            new Set(
                (appliedEntitiesData?.appliedEntities ?? []).map(
                    (applied) => applied.entityId,
                ),
            ),
        [appliedEntitiesData],
    );

    const appliedAtByEntityId = useMemo(
        () =>
            new Map(
                (appliedEntitiesData?.appliedEntities ?? []).map((applied) => [
                    applied.entityId,
                    formatISO(applied.appliedAt),
                ]),
            ),
        [appliedEntitiesData],
    );

    const {
        tableData,
        isLoading: isTreeLoading,
        isError: isTreeError,
    } = useApplyCoaTemplateEntityTree({
        workspaces,
        template,
        allowedEntityIds,
        appliedAtByEntityId,
    });

    // Same filtering as the apply Select-entities step (Services / Team are
    // client-level, Industry / Basis and name search per entity), plus the
    // drawer-specific Last-updated and Applied date ranges.
    const filteredTableData = useMemo<
        ApplyCoaTemplateEntitySelectionRow[]
    >(() => {
        const serviceTeamFiltered = filterWorkspaceRowsByServicesAndTeam({
            rows: tableData,
            selectedServices: filters.services ?? [],
            selectedTeamMemberIds: filters.teamMemberIds ?? [],
        });

        const nameIndustryBasisFiltered = filterSelectEntitiesRows({
            rows: serviceTeamFiltered,
            search: searchFilter,
            selectedIndustries: filters.entityIndustries ?? [],
            selectedBases: filters.entityBases ?? [],
        });

        const hasDateFilter =
            !!filters.coaLastUpdatedStart ||
            !!filters.coaLastUpdatedEnd ||
            !!filters.coaAppliedAtStart ||
            !!filters.coaAppliedAtEnd;
        if (!hasDateFilter) {
            return nameIndustryBasisFiltered;
        }

        return nameIndustryBasisFiltered.reduce<
            ApplyCoaTemplateEntitySelectionRow[]
        >((acc, workspaceRow) => {
            const children = (workspaceRow.children ?? []).filter(
                (child) =>
                    isWithinRange(
                        child.coaLastUpdatedAt,
                        filters.coaLastUpdatedStart,
                        filters.coaLastUpdatedEnd,
                    ) &&
                    isWithinRange(
                        child.appliedAt,
                        filters.coaAppliedAtStart,
                        filters.coaAppliedAtEnd,
                    ),
            );
            if (children.length > 0) {
                acc.push({ ...workspaceRow, children });
            }
            return acc;
        }, []);
    }, [tableData, searchFilter, filters]);

    const tableMeta = useMemo<
        ViewTableMeta<ApplyCoaEntityOpenCallbacks, never>
    >(() => ({ callbacks: { onOpenEntity: openEntityChartOfAccounts } }), []);

    const handleRowClick = useCallback(
        (row: ApplyCoaTemplateEntitySelectionRow | undefined) => {
            if (!row || row.isClientRow || row.entityId === null) {
                return;
            }
            openEntityChartOfAccounts({
                workspaceId: row.workspaceId,
                entityId: row.entityId,
            });
        },
        [],
    );

    if (isWorkspacesLoading || isAppliedLoading || isTreeLoading) {
        return <Loader />;
    }

    // Surface an error instead of a misleading "No entities found": a failed
    // applied-entities fetch leaves the id set empty (so the tree filters every
    // row out), and a failed enrichment/ledgers query blanks the columns.
    if (isWorkspacesError || isAppliedError || isTreeError) {
        return (
            <div className={styles.content}>
                <EmptyState header="Couldn't load entities. Please try again." />
            </div>
        );
    }

    return (
        <div className={styles.content}>
            <AppliedEntitiesToolbar
                searchFilter={searchFilter}
                onSearchChange={setSearchFilter}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                view={view}
                onViewChange={setView}
            />
            <ViewTable
                data={filteredTableData}
                view={view}
                config={applyCoaTemplateEntitySelectionViewConfig}
                className={styles.appliedEntitiesView}
                onViewChange={setView}
                tableMeta={tableMeta}
                enableColumnResizing
                emptyView={() => <EmptyState header="No entities found" />}
                loadingView={() => <Loader />}
                onRowClick={handleRowClick}
                autoExpandOnMount
            />
        </div>
    );
};

export const ChartOfAccountsTemplateAppliedEntitiesDrawer = ({
    open,
    template,
    onClose,
}: Props) => {
    const header = (
        <TemplateDrawerTrail title="Entities" templateName={template?.name} />
    );

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            size="fullScreen"
            noPadding
            headerAddonLeft={header}
        >
            {open && template && (
                <ChartOfAccountsTemplateAppliedEntitiesDrawerContent
                    template={template}
                />
            )}
        </Drawer>
    );
};
